import { z } from 'zod';

import { type LoggedUser } from '@/features/user/types/user-types';

export const PaywallValueSchema = z.enum([
  'paywall_from_direct_access',
  'paywall_from_menu_upgrade_button',
  'paywall_from_menu_messages_button',
  'paywall_from_header_messages_button',
  'paywall_from_user_profile_upgrade_button',
  'paywall_from_email_link',
]);

export type PaywallValue = z.infer<typeof PaywallValueSchema>;

export interface PaywallReport {
  userId: LoggedUser['id'],
  action: 'subscription_paywall_view',
  viewedFrom: PaywallValue,
}

export type ClickhouseReport = PaywallReport;

export type MessageSentSource = 'all-listings-list' | 'userprofile' | 'listingprofile' | 'search-grid' | 'mailbox';

export type MegaphoneSentSource = 'newlisting_megaphone' | 'megaphone';

export interface ElasticMegaphoneSentReport {
  action_name: 'MegaphoneSent',
  details: {
    src: MegaphoneSentSource,
    [key: string]: string | number,
  },
}

export interface ElasticMegaphonePageReport {
  action_name: 'megaphone_page_request',
  details: {
    src: 'header' | 'menu',
    [key: string]: string | number,
  },
}

export interface ElasticMailboxPageReport {
  action_name: 'mailbox_page_request',
  details: {
    src: 'header' | 'menu',
    [key: string]: string | number,
  },
}

export interface ElasticDeleteUserDataRequestReport {
  action_name: 'request_to_delete_user_data',
  details: {
    email: string,
    phone_number: string,
  },
  locale: string,
  version: string,
}

export interface ElasticPaywallVisitReport {
  action_name: 'subscription_paywall_view',
  details: {
    src: PaywallValue,
    [key: string]: string | number,
  },
}

export interface ElasticAddUserPhotoReport {
  action_name: 'user_photo_upload_button_clicked',
  details: {
    src: 'header',
    [key: string]: string | number,
  },
}

export interface ElasticAddListingPhotoReport {
  action_name: 'listing_photo_upload_button_clicked',
  details: {
    src: 'header',
    [key: string]: string | number,
  },
}

const PlaceReportUserSchema = z.object({
  logged_in: z.boolean(),
})

const PlaceReportLocationSchema = z.object({
  will_redirect: z.boolean(),
  initial_request: z.string(),
  final_response: z.string(),
  is_geo_page: z.boolean(),
})

export const PlaceReportDataSchema = z.object({
  user: PlaceReportUserSchema,
  location: PlaceReportLocationSchema.nullable(),
  is_found: z.boolean(),
  created_at_iso: z.string(),
})

export type PlaceReportData = z.infer<typeof PlaceReportDataSchema>;

export interface ElasticListingsSearchPageReport {
  action_name: 'listing_search_results_page_location_data',
  locale: string,
  details: {
    src: 'listings_search_results_page',
    data: PlaceReportData,
  },
}

export type ElasticReport =
  ElasticMegaphoneSentReport
  | ElasticPaywallVisitReport
  | ElasticAddUserPhotoReport
  | ElasticMegaphonePageReport
  | ElasticAddListingPhotoReport
  | ElasticListingsSearchPageReport
  | ElasticMailboxPageReport
  | ElasticDeleteUserDataRequestReport;
